/**
 * KennelComplianceNotice — RODO / transparentność dla stron hodowli
 * z katalogu (import publiczny / Google Places) BEZ przejętego właściciela.
 *
 * Parytet z BusinessComplianceNotice (firmy). Eksportuje:
 *  - KennelUnclaimedBanner   → górny disclaimer + 3 akcje (Przejmij / Popraw dane / Usuń stronę)
 *                              (sam pobiera status po slug — działa na dowolnym szablonie strony)
 *  - KennelDataSourceSection → sekcja "Źródło danych i Twoje prawa" (art. 14 RODO)
 *
 * Renderują się WYŁĄCZNIE gdy hodowla jest nieprzejęta (is_claimed === false).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Info } from 'lucide-react';
import { API_BASE as API } from '@/utils/api';
import ClaimReadyHero from '@/components/ClaimReadyHero';

/* ─────────────── Wspólny modal shell ─────────────── */
function ModalShell({ title, subtitle, accent = '#0ea5e9', onClose, children }) {
  return (
    <div
      data-testid="kennel-compliance-modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="kennel-compliance-modal"
        style={{
          background: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px',
          padding: '28px', maxWidth: '480px', width: '100%', color: '#e2e8f0',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.3 }}>{title}</h2>
          <button onClick={onClose} aria-label="Zamknij" data-testid="kennel-compliance-modal-close"
            style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '2px' }}>
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>
        {subtitle && <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 18px', lineHeight: 1.6 }}>{subtitle}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '11px 14px', background: '#1e293b', border: '1px solid #334155',
  borderRadius: '8px', color: '#e2e8f0', fontSize: '14px', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '12px', marginBottom: '6px', color: '#cbd5e1' };
const fieldWrap = { marginBottom: '14px' };

function primaryBtn(accent) {
  return {
    flex: 1, padding: '12px 18px', background: accent, color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  };
}
const secondaryBtn = {
  flex: 1, padding: '12px 18px', background: 'transparent', color: '#94a3b8',
  border: '1px solid #334155', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
};

/* ─────────────── Przejmij stronę (claim) ─────────────── */
function ClaimModal({ slug, entityName, onClose }) {
  const [form, setForm] = useState({ email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Podaj email'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/public/kennel-claim/${slug}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Sprawdź skrzynkę — wysłaliśmy link do edycji!'); }
      else toast.error(data.detail || 'Nie udało się wysłać zgłoszenia');
    } catch { toast.error('Błąd połączenia'); }
    finally { setSending(false); }
  };

  return (
    <ModalShell
      title={done ? 'Sprawdź skrzynkę!' : `Przejmij: ${entityName}`}
      subtitle={done ? null : 'Po weryfikacji emailem otrzymasz pełny dostęp — zdjęcia, opis, dane kontaktowe, a zapytania klientów zaczną trafiać bezpośrednio do Ciebie. Obecnie wszystkie funkcje są darmowe.'}
      accent="#0ea5e9" onClose={onClose}
    >
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '20px' }}>
            Wysłaliśmy link aktywacyjny na <strong style={{ color: '#fff' }}>{form.email}</strong>. Sprawdź też folder <em>Spam/Oferty</em>.
          </p>
          <button onClick={onClose} style={{ ...primaryBtn('#0ea5e9'), width: '100%' }} data-testid="kennel-claim-done-close">Zamknij</button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="kennel-claim-form">
          <div style={fieldWrap}>
            <label style={labelStyle}>Email *</label>
            <input data-testid="kennel-claim-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="twoj@email.pl" style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Telefon (opcjonalnie)</label>
            <input data-testid="kennel-claim-phone" type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+48 123 456 789" style={inputStyle} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: '20px' }}>
            <label style={labelStyle}>Wiadomość (opcjonalnie)</label>
            <textarea data-testid="kennel-claim-message" rows={3} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Prowadzę tę hodowlę..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={sending} style={secondaryBtn}>Anuluj</button>
            <button type="submit" disabled={sending} style={primaryBtn('#0ea5e9')} data-testid="kennel-claim-submit">
              {sending ? 'Wysyłam...' : 'Przejmij stronę →'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

/* ─────────────── Popraw dane / Zgłoś błąd (report) ─────────────── */
function ReportModal({ slug, entityName, onClose }) {
  const [form, setForm] = useState({ message: '', email: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error('Opisz co należy poprawić'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/public/kennel/${slug}/report`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'update', message: form.message, email: form.email }),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Dziękujemy — zgłoszenie przyjęte.'); }
      else toast.error(data.detail || 'Nie udało się wysłać');
    } catch { toast.error('Błąd połączenia'); }
    finally { setSending(false); }
  };

  return (
    <ModalShell
      title={done ? 'Zgłoszenie przyjęte' : 'Popraw dane hodowli'}
      subtitle={done ? null : `Opisz, jakie dane hodowli „${entityName}" są nieaktualne lub błędne. Sprawdzimy zgłoszenie i poprawimy wpis.`}
      accent="#f59e0b" onClose={onClose}
    >
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '20px' }}>
            Dziękujemy za zgłoszenie. Zajmiemy się poprawą danych. Najszybciej zaktualizujesz wpis przejmując stronę.
          </p>
          <button onClick={onClose} style={{ ...primaryBtn('#f59e0b'), width: '100%' }} data-testid="kennel-report-done-close">Zamknij</button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="kennel-report-form">
          <div style={fieldWrap}>
            <label style={labelStyle}>Co należy poprawić? *</label>
            <textarea data-testid="kennel-report-message" rows={4} required value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="np. nieaktualny telefon / adres / rasa..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: '20px' }}>
            <label style={labelStyle}>Twój email (opcjonalnie — odpiszemy)</label>
            <input data-testid="kennel-report-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="twoj@email.pl" style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={sending} style={secondaryBtn}>Anuluj</button>
            <button type="submit" disabled={sending} style={primaryBtn('#f59e0b')} data-testid="kennel-report-submit">
              {sending ? 'Wysyłam...' : 'Wyślij zgłoszenie'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

/* ─────────────── Usuń stronę (removal-request) ─────────────── */
function RemoveModal({ slug, entityName, onClose }) {
  const [form, setForm] = useState({ company_name: '', reason: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error('Podaj nazwę hodowli'); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error('Podaj email lub telefon do kontaktu'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/public/kennel/${slug}/removal-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) { setDone(true); toast.success('Strona hodowli została usunięta z katalogu.'); }
      else toast.error(data.detail || 'Nie udało się usunąć');
    } catch { toast.error('Błąd połączenia'); }
    finally { setSending(false); }
  };

  return (
    <ModalShell
      title={done ? 'Strona usunięta' : 'Usuń tę stronę'}
      subtitle={done ? null : 'Strona zostanie usunięta z katalogu, wyszukiwarki i podglądu publicznego. Potwierdź, że jesteś osobą uprawnioną do reprezentowania hodowli.'}
      accent="#ef4444" onClose={onClose}
    >
      {done ? (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '20px' }}>
            Strona <strong style={{ color: '#fff' }}>{entityName}</strong> została usunięta z katalogu i nie będzie już publicznie widoczna. W razie pytań napisz na <a href="mailto:kontakt@apisai.pl" style={{ color: '#38bdf8' }}>kontakt@apisai.pl</a>.
          </p>
          <button onClick={() => { onClose(); window.location.href = '/katalog?type=hodowle'; }} style={{ ...primaryBtn('#ef4444'), width: '100%' }} data-testid="kennel-remove-done-close">
            Zamknij
          </button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="kennel-remove-form">
          <div style={fieldWrap}>
            <label style={labelStyle}>Nazwa hodowli *</label>
            <input data-testid="kennel-remove-company" required value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder={entityName} style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Email kontaktowy</label>
            <input data-testid="kennel-remove-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="twoj@email.pl" style={inputStyle} />
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>lub Telefon</label>
            <input data-testid="kennel-remove-phone" type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+48 123 456 789" style={inputStyle} />
          </div>
          <div style={{ ...fieldWrap, marginBottom: '20px' }}>
            <label style={labelStyle}>Powód (opcjonalnie)</label>
            <textarea data-testid="kennel-remove-reason" rows={2} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="np. nie chcę być w katalogu" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={sending} style={secondaryBtn}>Anuluj</button>
            <button type="submit" disabled={sending} style={primaryBtn('#ef4444')} data-testid="kennel-remove-submit">
              {sending ? 'Usuwam...' : 'Usuń stronę'}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

/* ─────────────── GÓRNY BANER (disclaimer + akcje) ─────────────── */
export function KennelUnclaimedBanner({ slug }) {
  const [kennel, setKennel] = useState(null);
  const [modal, setModal] = useState(null); // 'claim' | 'report' | 'remove'

  useEffect(() => {
    if (!slug) return;
    let canceled = false;
    axios.get(`${API}/api/public/hodowla/${slug}`)
      .then((r) => { if (!canceled) setKennel(r.data?.kennel || null); })
      .catch(() => { if (!canceled) setKennel(null); });
    return () => { canceled = true; };
  }, [slug]);

  if (!kennel || kennel.is_claimed !== false) return null;
  const entityName = kennel.name || 'tej hodowli';

  return (
    <>
      <ClaimReadyHero
        kind="kennel"
        entityName={entityName}
        onClaim={() => setModal('claim')}
        onReport={() => setModal('report')}
        onRemove={() => setModal('remove')}
        testids={{ root: 'kennel-unclaimed-disclaimer-banner', claim: 'kennel-banner-claim-btn', report: 'kennel-banner-report-btn', remove: 'kennel-banner-remove-btn' }}
      />

      {modal === 'claim' && <ClaimModal slug={slug} entityName={entityName} onClose={() => setModal(null)} />}
      {modal === 'report' && <ReportModal slug={slug} entityName={entityName} onClose={() => setModal(null)} />}
      {modal === 'remove' && <RemoveModal slug={slug} entityName={entityName} onClose={() => setModal(null)} />}
    </>
  );
}

/* ─────────────── SEKCJA "ŹRÓDŁO DANYCH" (art. 14 RODO) ─────────────── */
export function KennelDataSourceSection({ kennel }) {
  if (!kennel || kennel.is_claimed !== false) return null;

  let importedStr = '';
  if (kennel.imported_at) {
    try { importedStr = new Date(kennel.imported_at).toLocaleDateString('pl-PL'); } catch { importedStr = ''; }
  }

  const rows = [
    { label: 'Źródło danych', value: kennel.data_source_label || 'Publicznie dostępne dane' },
    importedStr ? { label: 'Data dodania do katalogu', value: importedStr } : null,
    { label: 'Zakres danych', value: 'Nazwa, adres, telefon, strona www oraz inne publicznie dostępne informacje o hodowli.' },
    { label: 'Administrator danych', value: 'ApisAI — kontakt@apisai.pl' },
  ].filter(Boolean);

  return (
    <section id="zrodlo-danych" data-testid="kennel-data-source-section" style={{ background: '#0a0a0a' }}>
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '64px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <Info style={{ width: '18px', height: '18px', color: '#c9a96e' }} />
          <h2 style={{ fontSize: '22px', fontWeight: 500, color: '#f5f5f0', margin: 0 }}>
            Źródło danych i Twoje prawa
          </h2>
        </div>
        <div style={{ border: '1px solid #222', borderRadius: '12px', overflow: 'hidden' }}>
          {rows.map((r, i) => (
            <div key={r.label} data-testid={`kennel-data-source-row-${i}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '14px 20px', borderTop: i ? '1px solid #222' : 'none' }}>
              <span style={{ minWidth: '180px', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888' }}>{r.label}</span>
              <span style={{ flex: 1, fontSize: '14px', color: '#bbb' }}>{r.value}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '13px', lineHeight: 1.7, color: '#888', marginTop: '18px' }}>
          Zgodnie z art. 14 RODO informujemy, że dane tej strony pozyskano ze źródeł publicznie dostępnych. Jeśli reprezentujesz tę hodowlę, masz prawo do <strong style={{ color: '#bbb' }}>dostępu, sprostowania, sprzeciwu oraz usunięcia danych</strong>. Aby przejąć, poprawić lub usunąć tę stronę, skorzystaj z przycisków na górze strony lub napisz na{' '}
          <a href="mailto:kontakt@apisai.pl" style={{ color: '#c9a96e' }}>kontakt@apisai.pl</a>.{' '}
          Szczegóły w <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" style={{ color: '#c9a96e' }}>Polityce prywatności</a>.
        </p>
      </div>
    </section>
  );
}

export default KennelUnclaimedBanner;
